/**
 * @fileoverview Shared controlled `DashboardCanvasBuilder` component in `@firesquirrel/ui-admin`.
 * Used identically by `DashboardBuilderPage` (`tenant-web`) and `SystemDashboardBuilderPage` (`system-web`).
 * @module ui-admin/components/DashboardCanvasBuilder/DashboardCanvasBuilder
 *
 * Responsibilities: - Render the 12-column `react-grid-layout` dashboard canvas with `DashboardCanvasToolbar`,
 *                     inline `CanvasWidgetPalette` drawer (`SlideOverPanel`), per-widget `CanvasWidgetCard`
 *                     (with `⋮` overflow menu and sample-data toggle), and portaled `CanvasWidgetPropertiesPanel`
 *                     into `propertiesPortalTarget`.
 * Dependencies:     - React, react-dom (createPortal), react-grid-layout, lucide-react
 *                   - @firesquirrel/local-db (DashboardConfig, DashboardWidget, CatalogCube)
 *                   - ./DashboardCanvasToolbar, ./CanvasWidgetPalette, ./CanvasWidgetCard,
 *                     ./CanvasWidgetPropertiesPanel, ./dashboardCanvasDragUtils
 * Architecture:     - 100% controlled & presentation-only (`config` + `onChange`).
 *                     Zero Dexie reads, zero `apiClient` calls, zero `useAIChat` calls.
 * Notes:            - Matches `05-dashboard-canvas-builder-spec.md` and `DocumentCanvasBuilder.tsx`.
 */

import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import RGL from 'react-grid-layout';
import { BarChart3, Plus, Compass, X } from 'lucide-react';
import type { DashboardConfig, DashboardWidget, CatalogCube } from '@firesquirrel/local-db';
import type { FilterValue } from '../dashboard/WidgetRenderer';
import {
  DashboardCanvasToolbar,
  type DashboardSurfaceFilter,
} from './DashboardCanvasToolbar';
import { CanvasWidgetPalette } from './CanvasWidgetPalette';
import { CanvasWidgetCard } from './CanvasWidgetCard';
import { CanvasWidgetPropertiesPanel } from './CanvasWidgetPropertiesPanel';
import {
  addWidget,
  moveWidget,
  duplicateWidget,
  removeWidget,
  setWidgetEnabled,
  applyLayoutChange,
  normalizeDashboardConfig,
  type RglLayoutItem,
} from './dashboardCanvasDragUtils';
import type { WidgetType } from './widgetTypes';
import { resolveDashboardTheme } from '../DashboardThemeEditor/dashboardThemePresets';

const ReactGridLayout = RGL as any;

/**
 * @contract DashboardCanvasBuilderProps
 * Purpose: Props for the shared controlled `DashboardCanvasBuilder`.
 * Fields:  config, onChange, selectedWidgetId?, onSelectedIdChange?, activationDriven?,
 *          propertiesPortalTarget?, readOnly?, widgetDataMap?, dataCatalog?, lookupOptionsByType?,
 *          refreshKey?, filterValues?, onFilterChange?, onOpenHelp?, onStartTour?
 * Rules:   Presentation-only; all mutations emit a new `DashboardConfig` via `onChange`.
 * Sync:    N/A (host owns persistence).
 */
export interface DashboardCanvasBuilderProps {
  config: DashboardConfig;
  onChange: (config: DashboardConfig) => void;
  selectedWidgetId?: string | null;
  onSelectedIdChange?: (id: string | null) => void;
  activationDriven?: boolean;
  propertiesPortalTarget?: HTMLElement | null;
  readOnly?: boolean;
  widgetDataMap?: Record<string, Record<string, unknown>[]>;
  dataCatalog?: CatalogCube[];
  lookupOptionsByType?: Record<string, Array<{ value: string; label: string }>>;
  refreshKey?: number;
  filterValues?: Record<string, FilterValue | null>;
  onFilterChange?: (filterId: string, value: FilterValue | null) => void;
  onOpenHelp?: () => void;
  onStartTour?: () => void;
}

/**
 * @component DashboardCanvasBuilder
 * Purpose:     Drag-and-drop 12-column dashboard widget canvas (controlled), shared by
 *              `tenant-web` and `system-web`.
 * Props:       DashboardCanvasBuilderProps.
 * Behavior:    Pure widget operations from `dashboardCanvasDragUtils`; no API/Dexie calls.
 *              RGL 2D drag/resize + toolbar surface filter + `SlideOverPanel` palette drawer +
 *              per-widget overflow menu + portaled properties panel.
 * Data Source: none (controlled; widget preview rows come from `widgetDataMap`).
 * Constraints: presentation-only; no persistence.
 * Performance: normalized config and visible widget list memoized.
 */
export function DashboardCanvasBuilder({
  config,
  onChange,
  selectedWidgetId: controlledSelectedId,
  onSelectedIdChange,
  activationDriven = true,
  propertiesPortalTarget = null,
  readOnly = false,
  widgetDataMap = {},
  dataCatalog,
  lookupOptionsByType = {},
  refreshKey,
  filterValues,
  onFilterChange,
  onOpenHelp,
  onStartTour,
}: DashboardCanvasBuilderProps) {
  const [internalSelectedId, setInternalSelectedId] = useState<string | null>(null);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [surfaceFilter, setSurfaceFilter] = useState<DashboardSurfaceFilter>('all');
  const [hiddenSampleIds, setHiddenSampleIds] = useState<Record<string, boolean>>({});
  const [tourBannerDismissed, setTourBannerDismissed] = useState(false);

  const selectedWidgetId =
    controlledSelectedId !== undefined ? controlledSelectedId : internalSelectedId;

  const selectWidget = (id: string | null) => {
    if (controlledSelectedId === undefined) {
      setInternalSelectedId(id);
    }
    onSelectedIdChange?.(id);
  };

  const normalized = useMemo(() => normalizeDashboardConfig(config), [config]);
  const resolvedTheme = useMemo(() => resolveDashboardTheme(normalized.theme), [normalized.theme]);

  const visibleWidgets = useMemo(() => {
    return normalized.widgets.filter((w) => {
      if (activationDriven && w.enabled === false) return false;
      if (surfaceFilter === 'kpis-charts') {
        return (
          w.type === 'kpi' ||
          w.type === 'chart' ||
          w.type === 'gauge' ||
          w.type === 'progressBar'
        );
      }
      if (surfaceFilter === 'tables-filters') {
        return (
          w.type === 'table' ||
          w.type === 'filter' ||
          w.type === 'list' ||
          w.type === 'text'
        );
      }
      return true;
    });
  }, [normalized.widgets, activationDriven, surfaceFilter]);

  const selectedWidget = useMemo(
    () => normalized.widgets.find((w) => w.id === selectedWidgetId) ?? null,
    [normalized.widgets, selectedWidgetId],
  );

  const handleAddWidget = (type: WidgetType) => {
    const next = addWidget(normalized, type);
    const added = next.widgets[next.widgets.length - 1];
    onChange(next);
    if (added) {
      selectWidget(added.id);
    }
  };

  const handleUpdateWidget = (widgetId: string, updates: Partial<DashboardWidget>) => {
    onChange({
      ...normalized,
      widgets: normalized.widgets.map((w) => (w.id === widgetId ? { ...w, ...updates } : w)),
    });
  };

  const handleDeleteWidget = (widgetId: string) => {
    const next = removeWidget(normalized, widgetId);
    onChange(next);
    if (selectedWidgetId === widgetId) {
      selectWidget(null);
    }
  };

  const handleDuplicateWidget = (widgetId: string) => {
    const next = duplicateWidget(normalized, widgetId);
    onChange(next);
  };

  const handleMoveWidget = (widgetId: string, dir: -1 | 1) => {
    const next = moveWidget(normalized, widgetId, dir);
    onChange(next);
  };

  const handleToggleEnabled = (widgetId: string, enabled: boolean) => {
    const next = setWidgetEnabled(normalized, widgetId, enabled);
    onChange(next);
  };

  const handleLayoutChange = (nextLayout: RglLayoutItem[]) => {
    if (readOnly) return;
    // Only commit when coordinates actually changed to avoid extra renders on mount
    const changed = nextLayout.some((item) => {
      const existing = normalized.widgets.find((w) => w.id === item.i);
      if (!existing) return false;
      return (
        existing.layout.x !== item.x ||
        existing.layout.y !== item.y ||
        existing.layout.w !== item.w ||
        existing.layout.h !== item.h
      );
    });
    if (changed) {
      onChange(applyLayoutChange(normalized, nextLayout));
    }
  };

  const rglLayout = useMemo(
    () =>
      visibleWidgets.map((w) => ({
        i: w.id,
        x: w.layout.x,
        y: w.layout.y,
        w: w.layout.w,
        h: w.layout.h,
        minW: w.layout.minW ?? 2,
        minH: w.layout.minH ?? 1,
        static: readOnly,
      })),
    [visibleWidgets, readOnly],
  );

  const propertiesPanelElement = (
    <CanvasWidgetPropertiesPanel
      widget={selectedWidget}
      onUpdate={(updates) => {
        if (selectedWidget) handleUpdateWidget(selectedWidget.id, updates);
      }}
      onDelete={() => {
        if (selectedWidget) handleDeleteWidget(selectedWidget.id);
      }}
      onDuplicate={() => {
        if (selectedWidget) handleDuplicateWidget(selectedWidget.id);
      }}
      isReadOnly={readOnly}
      dataCatalog={dataCatalog}
    />
  );

  return (
    <div
      data-testid="dashboard-canvas-builder"
      className="flex flex-col h-full min-h-0 overflow-hidden"
      style={{
        backgroundColor: resolvedTheme.pageBg,
        fontFamily: resolvedTheme.fontFamily,
      }}
    >
      <DashboardCanvasToolbar
        surfaceFilter={surfaceFilter}
        onSurfaceFilterChange={setSurfaceFilter}
        columns={normalized.layout.columns}
        rowHeight={normalized.layout.rowHeight}
        widgetCount={visibleWidgets.length}
        onAddWidgetClick={() => setPaletteOpen(true)}
        onOpenHelp={onOpenHelp}
        onStartTour={onStartTour}
        readOnly={readOnly}
      />

      {!readOnly && onStartTour && !tourBannerDismissed && (
        <div
          data-testid="dashboard-tour-prompt-banner"
          className="mx-6 mt-4 px-4 py-2.5 rounded-lg bg-orange-50 border border-orange-200 flex items-center justify-between text-xs text-orange-900 shrink-0"
        >
          <span className="flex items-center gap-2 font-medium">
            <Compass className="h-4 w-4 text-orange-600 shrink-0" />
            New to the dashboard builder? Take a quick guided tour of the 3-pane workspace.
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onStartTour}
              className="px-2.5 py-1 rounded-md bg-orange-600 text-white font-semibold hover:bg-orange-700"
            >
              Start tour
            </button>
            <button
              type="button"
              aria-label="Dismiss tour banner"
              onClick={() => setTourBannerDismissed(true)}
              className="p-1 rounded hover:bg-orange-100 text-orange-700"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-6">
        {visibleWidgets.length === 0 ? (
          <div
            data-testid="dashboard-canvas-empty"
            className="flex flex-col items-center justify-center h-80 text-center bg-white/80 rounded-xl border-2 border-dashed border-gray-300 p-8"
          >
            <BarChart3 className="h-12 w-12 text-gray-300 mb-3" />
            <h3 className="text-base font-semibold text-gray-700 mb-1">No widgets yet</h3>
            <p className="text-xs text-gray-500 max-w-sm mb-4">
              {readOnly
                ? 'This dashboard does not have any enabled widgets.'
                : 'Click "Add widget" to add KPI cards, ECharts, data tables, or global filters to your 12-column dashboard canvas.'}
            </p>
            {!readOnly && (
              <button
                type="button"
                data-testid="empty-state-add-widget-btn"
                onClick={() => setPaletteOpen(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-orange-600 hover:bg-orange-700 rounded-lg shadow-xs transition-colors"
              >
                <Plus className="h-4 w-4" />
                Add widget
              </button>
            )}
          </div>
        ) : (
          <ReactGridLayout
            className="layout"
            layout={rglLayout}
            cols={normalized.layout.columns || 12}
            rowHeight={normalized.layout.rowHeight || 80}
            width={1100}
            onLayoutChange={handleLayoutChange}
            draggableHandle=".drag-handle"
            isDraggable={!readOnly}
            isResizable={!readOnly}
            compactType="vertical"
            preventCollision={false}
          >
            {visibleWidgets.map((widget, index) => {
              const lookupKey = widget.filterConfig?.lookupType;
              const lookupOptions = lookupKey ? lookupOptionsByType[lookupKey] : undefined;
              return (
                <div key={widget.id} className="h-full">
                  <CanvasWidgetCard
                    widget={widget}
                    isSelected={selectedWidgetId === widget.id}
                    isFirst={index === 0}
                    onSelect={() => selectWidget(widget.id)}
                    onDelete={() => handleDeleteWidget(widget.id)}
                    onDuplicate={() => handleDuplicateWidget(widget.id)}
                    onMoveUp={() => handleMoveWidget(widget.id, -1)}
                    onMoveDown={() => handleMoveWidget(widget.id, 1)}
                    onToggleEnabled={(enabled) => handleToggleEnabled(widget.id, enabled)}
                    onToggleSample={() =>
                      setHiddenSampleIds((prev) => ({
                        ...prev,
                        [widget.id]: !prev[widget.id],
                      }))
                    }
                    showSample={!hiddenSampleIds[widget.id]}
                    data={widgetDataMap[widget.id]}
                    readOnly={readOnly}
                    refreshKey={refreshKey}
                    filterValues={filterValues}
                    onFilterChange={onFilterChange}
                    lookupOptions={lookupOptions}
                  />
                </div>
              );
            })}
          </ReactGridLayout>
        )}
      </div>

      {!readOnly && (
        <CanvasWidgetPalette
          open={paletteOpen}
          onClose={() => setPaletteOpen(false)}
          onAddWidget={handleAddWidget}
        />
      )}

      {propertiesPortalTarget ? createPortal(propertiesPanelElement, propertiesPortalTarget) : null}
    </div>
  );
}

export default DashboardCanvasBuilder;
